/**
 * Neurodivergent Profile Selector
 *
 * Checkbox card grid for selecting neurodivergent accommodation profiles.
 * Multi-select with privacy notice.
 */

import { motion } from 'framer-motion'
import type { NeurodivergentProfile } from '@personic/shared'

interface NeurodivergentSelectorProps {
  profiles: NeurodivergentProfile[]
  value: string[]
  onChange: (profiles: string[]) => void
  isLoading?: boolean
}

const profileIcons: Record<string, string> = {
  adhd: '⚡',
  autism: '🧩',
  dyslexia: '📖',
  anxiety: '🌊',
}

export default function NeurodivergentSelector({
  profiles,
  value,
  onChange,
  isLoading,
}: NeurodivergentSelectorProps) {
  const toggleProfile = (name: string) => {
    if (value.includes(name)) {
      onChange(value.filter((p) => p !== name))
    } else {
      onChange([...value, name])
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-56 bg-gray-200 rounded animate-pulse" />
        <div className="grid md:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-36 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Neurodivergent Accommodations
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Select profiles to adjust baseline expectations (multi-select)
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {profiles.map((profile) => {
          const isSelected = value.includes(profile.name)
          const icon = profileIcons[profile.name] || '🧠'
          return (
            <motion.button
              key={profile.name}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => toggleProfile(profile.name)}
              className={`text-left p-4 rounded-xl border-2 transition-colors ${
                isSelected
                  ? 'border-engage-primary bg-engage-primary/5 ring-2 ring-engage-primary/20'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${
                      isSelected ? 'bg-engage-primary/10' : 'bg-gray-100'
                    }`}
                  >
                    {icon}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900 text-sm">
                      {profile.description}
                    </h4>
                    {isSelected && (
                      <div className="w-4 h-4 bg-engage-primary rounded flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-2.5 h-2.5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {Object.entries(profile.accommodations)
                      .slice(0, 3)
                      .map(([key, val]) => (
                        <span
                          key={key}
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            isSelected
                              ? 'bg-engage-primary/10 text-engage-primary'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {key}: {val}
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            </motion.button>
          )
        })}
      </div>

      {/* Privacy Notice */}
      <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
        <span className="text-blue-500 mt-0.5">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </span>
        <p className="text-xs text-blue-700 leading-relaxed">
          These selections adjust baseline expectations only. They are never stored
          as diagnostic labels and do not appear in reports or exports.
        </p>
      </div>
    </div>
  )
}
